import { useId, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { lookAxis, type SettingLookCode } from '@gk/contracts'
import { clockVec, dialLayout, dialSpokeHeading, drawLook, genericShape, shapePath, type DialLayout, type LookFrame } from '../../data/lookGeometry.ts'
import { useElementWidth } from '../hooks.ts'
import { LookMarks, LookPicture, useClipId } from './LookPicture.tsx'

/** A choice as the picker offers it: the club's word (its value and its text) and its picture, or none (`data/plan.ts` `choiceOptions` builds these
 *  from a task type; the set-up's library tiles pass a picture's name as the label). */
export interface LookOption { value: string; label: string; look: SettingLookCode | null }

/** M25-P (docs/M25.md E, rule 10): A PICKER INSTEAD OF A LIST. One radio per choice, named by the club's word:
 *  - tiles of 56 px at least, the picture above the word (13 px and up); a choice with no picture shows its word alone;
 *  - a DIAL when every choice has a direction picture (and no two would share a spoke): the choices as spokes around a green that shows the chosen
 *    stripes, each spoke ending in its 56-px button; the previous cut's axis dashed when the page gives it — only a page whose viewer holds
 *    `worklog.read` does (rule 2) — with the sentence that says so. The dial falls back to tiles where it would not fit the width it is given.
 *  `none` adds a choice that picks nothing (« Non prévu » at the finish): it answers null. The picker reads picture codes, never a word (rule 4). */
export function LookPicker({ legend, options, value, onChange, frame = 'round', none, previous = null, layout = 'auto', disabled = false, testId }: {
  legend: ReactNode; options: readonly LookOption[]; value: string | null; onChange: (value: string | null) => void; frame?: LookFrame
  none?: string; previous?: SettingLookCode | null; layout?: 'auto' | 'tiles'; disabled?: boolean; testId?: string
}) {
  const name = `gk-pick-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`
  const [measure, width] = useElementWidth<HTMLFieldSetElement>()
  const spokes = useMemo(() => (layout === 'auto' ? dialHeadings(options) : null), [layout, options])
  const dial = useMemo(() => (spokes ? dialLayout(spokes) : null), [spokes])
  const useDial = !!dial && (width === null || width >= dial.width)
  const tile = (opt: LookOption | null) => {
    const v = opt ? opt.value : null, checked = value === v
    return (
      <Tile key={opt ? `o:${opt.value}` : 'none'} name={name} value={opt?.value ?? ''} checked={checked} disabled={disabled} onPick={() => onChange(v)}>
        {opt?.look && <LookPicture code={opt.look} frame={frame} size="schema" className="size-10 shrink-0 text-brand dark:text-fairway" />}
        <span data-word className="text-[13px] font-semibold leading-tight">{opt ? opt.label : none}</span>
      </Tile>
    )
  }
  return (
    <fieldset ref={measure} className="min-w-0" data-testid={testId} disabled={disabled}>
      <legend className="mb-2 font-semibold">{legend}</legend>
      {useDial
        ? <Dial layout={dial!} headings={spokes!} options={options} value={value} onChange={onChange} previous={previous} name={name} disabled={disabled} testId={testId} />
        : <div className="grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] gap-2">{options.map((o) => tile(o))}{none !== undefined && tile(null)}</div>}
      {useDial && none !== undefined && <div className="mt-2 grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] gap-2">{tile(null)}</div>}
    </fieldset>
  )
}

/** the spokes' headings, when every choice has a direction picture and no two share a spoke; else null (tiles) */
function dialHeadings(options: readonly LookOption[]): number[] | null {
  if (options.length < 2) return null
  const hs = options.map((o) => (o.look ? dialSpokeHeading(o.look) : null))
  if (hs.some((h) => h === null)) return null
  return new Set(hs).size === hs.length ? (hs as number[]) : null
}

function Tile({ name, value, checked, disabled, onPick, children }: { name: string; value: string; checked: boolean; disabled: boolean; onPick: () => void; children: ReactNode }) {
  return (
    <label data-tile data-value={value} data-checked={checked ? 'true' : 'false'}
      className={`relative flex min-h-14 min-w-14 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 px-2 py-2 text-center has-[:focus-visible]:outline-4 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand ${
        disabled ? 'cursor-not-allowed border-slate-300 bg-slate-100 text-slate-600 dark:border-night-line dark:bg-night-line dark:text-slate-400'
          : checked ? 'border-brand bg-fairway-light text-slate-900 dark:border-emerald-500 dark:bg-night dark:text-white' : 'border-slate-300 bg-white text-slate-900 active:bg-fairway-light dark:border-night-line dark:bg-night-card dark:text-slate-50 dark:active:bg-night'}`}>
      <input type="radio" className="sr-only" name={name} value={value} checked={checked} disabled={disabled} onChange={onPick} />
      {children}
    </label>
  )
}

/** the length from a button's centre to its edge along a direction: where a spoke stops short of its button */
const toEdge = (d: { x: number; y: number }, w: number, h: number) => Math.min(Math.abs(d.x) > 1e-9 ? w / 2 / Math.abs(d.x) : Infinity, Math.abs(d.y) > 1e-9 ? h / 2 / Math.abs(d.y) : Infinity)
/** the radius of the green's ellipse along a direction */
const greenRadius = (L: DialLayout, d: { x: number; y: number }) => 1 / Math.sqrt((d.x / L.green.rx) ** 2 + (d.y / L.green.ry) ** 2)

function Dial({ layout: L, headings, options, value, onChange, previous, name, disabled, testId }: {
  layout: DialLayout; headings: readonly number[]; options: readonly LookOption[]; value: string | null; onChange: (v: string | null) => void
  previous: SettingLookCode | null; name: string; disabled: boolean; testId?: string
}) {
  const { t } = useTranslation()
  const clipId = useClipId()
  const selected = options.find((o) => o.value === value)?.look ?? null
  // the green: the generic round shape of the library, scaled so that its ellipse is the dial's green, the chosen stripes laid on it
  const round = useMemo(() => genericShape('round', 48), [])
  const drawing = useMemo(() => (selected ? drawLook(selected, round, 48) : null), [selected, round])
  const gw = (2 * L.green.rx) / 0.8, gh = (2 * L.green.ry) / 0.86
  const prevAxis = previous ? lookAxis(previous) : null
  const P = (h: number, r: number) => { const d = clockVec(h); return { x: L.c.x + d.x * r, y: L.c.y + d.y * r } }
  const n = (v: number) => Math.round(v * 10) / 10
  return (
    <>
      <div className="relative mx-auto" style={{ width: L.width, height: L.height }} data-testid={testId ? `${testId}-dial` : undefined}>
        <svg className="absolute inset-0 text-slate-500 dark:text-slate-400" width={L.width} height={L.height} viewBox={`0 0 ${n(L.width)} ${n(L.height)}`} fill="none" stroke="currentColor" strokeLinecap="round" aria-hidden="true" focusable="false">
          {options.map((o, i) => {
            const h = headings[i]!, d = clockVec(h), on = o.value === value
            const from = P(h, greenRadius(L, d) + 3), to = P(h, L.radius - toEdge(d, L.buttonW, L.buttonH) - 4)
            return <path key={o.value} d={`M${n(from.x)} ${n(from.y)}L${n(to.x)} ${n(to.y)}`} strokeWidth={on ? 3.5 : 2} strokeOpacity={on ? 1 : 0.55}
              className={on ? 'text-brand dark:text-fairway' : undefined} data-spoke-line data-value={o.value} data-selected={on ? 'true' : 'false'} />
          })}
          <svg x={n(L.c.x - gw / 2)} y={n(L.c.y - gh / 2)} width={n(gw)} height={n(gh)} viewBox="0 0 48 48" overflow="visible" className="text-brand dark:text-fairway">
            {drawing ? <LookMarks drawing={drawing} clipId={clipId} /> : <path d={shapePath(round)} strokeWidth={1.9} />}
          </svg>
          {prevAxis !== null && (() => {
            const d = clockVec(prevAxis), r = greenRadius(L, d) + 10, a = P(prevAxis, -r), b = P(prevAxis, r)
            return <path d={`M${n(a.x)} ${n(a.y)}L${n(b.x)} ${n(b.y)}`} strokeWidth={2.2} strokeDasharray="6 5" className="text-slate-700 dark:text-slate-300" data-mark="previous" />
          })()}
        </svg>
        {options.map((o, i) => {
          const b = L.buttons[i]!, on = o.value === value
          return (
            <label key={o.value} data-spoke data-value={o.value} data-checked={on ? 'true' : 'false'} style={{ left: `${n(b.left)}px`, top: `${n(b.top)}px`, width: `${L.buttonW}px` }}
              className={`absolute flex min-h-14 cursor-pointer items-center justify-center rounded-xl border-2 px-1.5 py-1 text-center has-[:focus-visible]:outline-4 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand ${
                disabled ? 'cursor-not-allowed border-slate-300 bg-slate-100 text-slate-600 dark:border-night-line dark:bg-night-line dark:text-slate-400'
                  : on ? 'border-brand bg-fairway-light text-slate-900 dark:border-emerald-500 dark:bg-night dark:text-white' : 'border-slate-300 bg-white text-slate-900 active:bg-fairway-light dark:border-night-line dark:bg-night-card dark:text-slate-50'}`}>
              <input type="radio" className="sr-only" name={name} value={o.value} checked={on} disabled={disabled} onChange={() => onChange(o.value)} />
              <span data-word className="min-w-0 text-[13px] font-semibold leading-tight [overflow-wrap:anywhere]">{o.label}</span>
            </label>
          )
        })}
      </div>
      {prevAxis !== null && <p className="meta mt-1">{t('looks.dial.previous')}</p>}
    </>
  )
}
